<template>
  <div w-full min-h-lg ref="mapRef"></div>
</template>
<script setup>
import "maplibre-gl/dist/maplibre-gl.css";

const mapRef = ref();

const props = defineProps({
  options: {
    type: Object,
    default: {},
  },
  sources: {
    type: Array,
    default: [],
  },
  layers: {
    type: Array,
    default: [],
  },
  marker: {
    type: [Boolean, Object],
    default: false,
  },
});

const emit = defineEmits(["load", "markerMove", "click", "interact"]);
let maplibregl, map, marker;
let loaded = false;

function addMarker() {
  if (marker) {
    return;
  }
  const markerOptions = typeof props.marker === "object" ? props.marker : {};
  marker = new maplibregl.Marker({
    draggable: true,
    color: "#1e86e9",
    ...markerOptions,
  })
    .setLngLat(props.options.center)
    .addTo(map);
  marker.on("dragend", () => {
    emit("markerMove", marker.getLngLat());
  });
}

async function addMap() {
  if (map) {
    return;
  }
  maplibregl = await loadMaplibre();
  if (map || unmounted) {
    return;
  }
  const defaultOptions = {
    container: mapRef.value,
    style: "/styles/light.json",
    center: [0, 0],
    zoom: 0,
    attributionControl: false,
  };

  map = new maplibregl.Map({
    ...defaultOptions,
    ...props.options,
  });

  map.on("load", () => {
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    // MapWrapper shows the attribution (MapAttribution), and collapses it when the map is first dragged, as
    // MapLibre's compact control did
    map.once("drag", () => emit("interact"));
    props.sources.forEach((s) => {
      const source = { ...s };
      const id = source.id;
      delete source.id;
      map.addSource(id, source);
    });
    props.layers.forEach((l) => {
      const layer = { ...l };
      if (!map.getLayer(layer.id)) {
        const beforeId = layer?.beforeId;
        delete layer.beforeId;
        map.addLayer(layer, beforeId);
      }
    });
    if (props.marker && props.options.center) {
      addMarker();
    }
    // on click print the features under the mouse
    map.on("click", (e) => {
      const features = map.queryRenderedFeatures(e.point);
      console.log(features);
      emit("click", features);
    });
    loaded = true;
    emit("load", map);
  });
}

let observer = null;

onMounted(async () => {
  if (isBot()) {
    return;
  }
  // needed for client component to access rendered template in onMounted
  await nextTick();
  if (!window.IntersectionObserver) {
    addMap();
    return;
  }
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        addMap();
        observer.disconnect();
      }
    },
    { rootMargin: "500px 0px" }
  );
  observer.observe(mapRef.value);
});

let unmounted = false;
onUnmounted(() => {
  unmounted = true;
  map?.remove();
  observer?.disconnect();
});

watch(props, (oldProps, newProps) => {
  // the load handler adds whatever the props are by then. After that, apply every change, even while tiles are
  // loading (when isStyleLoaded() is false): e.g. contour lines, which MapContour adds while the map may be moving.
  if (!map || !loaded) {
    return;
  }

  // update sources
  newProps.sources.forEach((source) => {
    if (source?.id && !map.getSource(source.id)) {
      // console.log("add source", source.id);
      map.addSource(source.id, source);
    } else {
      // console.log("update source", source.id);
      if (source.type === "geojson") {
        map.getSource(source.id).setData(source.data);
      } else if (source.type === "image") {
        map.getSource(source.id).updateImage({ url: source.url, coordinates: source.coordinates });
      } else if (source.tiles) {
        // only reload tiles when they change, e.g. contour units switching between ft and m
        const mapSource = map.getSource(source.id);
        if (JSON.stringify(mapSource.tiles) !== JSON.stringify(source.tiles)) {
          mapSource.setTiles(source.tiles);
        }
      } else {
        map.getSource(source.id).setUrl(source.url);
      }
    }
  });

  // update layers
  oldProps.layers.forEach((layer) => {
    if (map.getLayer(layer.id)) {
      // console.log("remove layer", layer.id);
      map.removeLayer(layer.id);
    }
  });
  newProps.layers.forEach((layer) => {
    if (!map.getLayer(layer.id)) {
      // console.log("add layer", layer.id);
      if (layer.beforeId) {
        map.addLayer(layer, layer.beforeId);
      } else {
        map.addLayer(layer);
      }
    }
  });

  // update marker and center
  if (newProps.marker && newProps.options.center) {
    if (!marker) {
      addMarker();
      marker?.addTo(map);
    }
    let markerCenter = marker?.getLngLat();
    marker?.setLngLat(newProps.options.center);

    if (markerCenter?.lng !== newProps.options.center[0] || markerCenter?.lat !== newProps.options.center[1]) {
      map.panTo(newProps.options.center);
    }
  } else {
    marker?.remove();
  }
});
</script>
